import { Fragment, useMemo } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
  type WeightUnit,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import {
  formatAddressLines,
  getQuoteEffectiveDate,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
} from "../utils";

interface PublicQuoteTemplateIndustrialQuotationProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const formatDate = (value?: Date | string | null) => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

const formatItemDescription = (line: PublicQuoteItemLine) => {
  const details: string[] = [];
  const itemName = line.item?.name ?? null;

  if (line.description && line.description !== itemName) {
    details.push(line.description);
  }

  if (line.item?.revision) {
    details.push(`Rev ${line.item.revision}`);
  }

  if (details.length === 0) {
    return null;
  }

  return details.join(" | ");
};

/**
 * Formats an item line's quantity breaks as cost estimate rows.
 * @param line - The item line to format
 * @param quote - The public quote, for currency formatting
 * @returns One cost estimate row per break with a numeric quantity
 */
const formatItemCostEstimateLines = (
  line: PublicQuoteItemLine,
  quote: PublicQuote,
) => {
  const unitLabel = "units";

  return line.quantityBreaks
    .map((quantityBreak) => {
      const quantity = quantityBreak.quantity?.trim();
      if (!quantity) {
        return null;
      }

      const unitPrice =
        quantityBreak.unitPriceOverride ?? quantityBreak.unitPrice;
      if (!unitPrice) {
        return null;
      }

      const quantityValue = Number.parseFloat(quantity);
      if (Number.isNaN(quantityValue)) {
        return null;
      }

      return {
        quantityValue,
        quantity: `${quantity} ${unitLabel}`,
        unitPrice: `${formatQuoteUnitPrice(quantityBreak, quote)} per unit`,
        totalPrice: formatQuoteLineTotal(quantityBreak, quote),
        totalWeight: quantityBreak.totalWeight ?? null,
        totalWeightUnit: getBreakWeightUnit(quantityBreak, line),
      };
    })
    .filter(
      (
        entry,
      ): entry is {
        quantityValue: number;
        quantity: string;
        unitPrice: string;
        totalPrice: string;
        totalWeight: string | null;
        totalWeightUnit: WeightUnit | null;
      } => entry !== null,
    );
};

/**
 * Renders an address as a block of text lines.
 * @param addressLines - Pre-formatted address lines from formatAddressLines
 * @param className - Optional CSS class name for the container
 * @returns JSX block or null if no lines
 */
const renderAddressBlock = (
  addressLines: string[] | null,
  className = "space-y-0.5 text-xs text-slate-700",
) => {
  if (!addressLines) {
    return null;
  }

  return (
    <div className={className}>
      {addressLines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
};

const TABLE_LABEL_CELL_CLASS =
  "w-[34%] border border-slate-400 bg-slate-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-700 align-middle";
const TABLE_VALUE_CELL_CLASS =
  "border border-slate-400 px-2 py-2 text-sm text-slate-900 align-middle";

const renderLineRows = (
  line: PublicQuoteLine,
  index: number,
  firstItemIndex: number,
  showTotals: boolean,
  showTotalWeight: boolean,
  quote: PublicQuote,
) => {
  if (line.type === "SECTION") {
    if (!line.description) {
      return null;
    }
    return (
      <TableRow key={`section-${index}`}>
        <TableCell
          colSpan={2}
          className="border border-slate-400 bg-slate-100 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-800"
        >
          {line.description}
        </TableCell>
      </TableRow>
    );
  }

  if (line.type === "NOTE") {
    if (!line.description) {
      return null;
    }
    return (
      <TableRow key={`note-${index}`}>
        <TableCell
          colSpan={2}
          className="border border-slate-400 px-2 py-2 text-sm text-slate-800"
        >
          <MarkdownReact>{line.description}</MarkdownReact>
        </TableCell>
      </TableRow>
    );
  }

  if (line.type === "DISCOUNT") {
    if (!showTotals) {
      return null;
    }

    const discountAmount =
      line.amount !== null ? formatQuoteLineTotal(line, quote) : null;

    return (
      <TableRow key={`discount-${index}`}>
        <TableCell className={TABLE_VALUE_CELL_CLASS}>
          {line.description || "Discount"}
        </TableCell>
        <TableCell className="border border-slate-400 px-2 py-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap align-middle">
          {discountAmount ? `-${discountAmount}` : ""}
        </TableCell>
      </TableRow>
    );
  }

  if (line.type === "ITEM") {
    const itemDescription = formatItemDescription(line);
    const itemCostEstimateLines = formatItemCostEstimateLines(line, quote);
    const itemTitle = line.item?.name || line.description || "Item";
    const hasSingleQuantity = itemCostEstimateLines.length === 1;
    const hasSingleUnit = hasSingleQuantity
      ? itemCostEstimateLines[0].quantityValue === 1
      : false;
    const singleCostEstimateLine = hasSingleUnit
      ? itemCostEstimateLines[0]
      : null;
    const hasEmphasizedTopBorder = index !== firstItemIndex;

    return (
      <Fragment key={`item-${index}`}>
        <TableRow>
          <TableCell
            className={`${TABLE_LABEL_CELL_CLASS} ${
              hasEmphasizedTopBorder ? "border-t-2 border-t-slate-400" : ""
            }`}
          >
            Item Name
          </TableCell>
          <TableCell
            className={`border border-slate-400 px-2 py-2 text-sm font-semibold text-slate-900 align-middle ${
              hasEmphasizedTopBorder ? "border-t-2 border-t-slate-400" : ""
            }`}
          >
            {itemTitle}
          </TableCell>
        </TableRow>
        {itemDescription && (
          <TableRow>
            <TableCell className="w-[34%] border border-slate-400 border-t-0 bg-slate-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-700 align-middle">
              Item Description
            </TableCell>
            <TableCell className="whitespace-pre-line border border-slate-400 border-t-0 px-2 py-2 text-sm text-slate-900 align-middle">
              {itemDescription}
            </TableCell>
          </TableRow>
        )}
        {itemCostEstimateLines.length > 0 && (
          <TableRow>
            <TableCell className="w-[34%] border border-slate-400 border-t-0 bg-slate-50 py-0 px-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-700 align-middle">
              Cost Estimate
            </TableCell>
            <TableCell
              className={`border border-slate-400 border-t-0 text-sm text-slate-900 align-middle ${
                hasSingleUnit ? "px-2 py-1.5" : "px-0 py-0"
              }`}
            >
              {singleCostEstimateLine ? (
                <div className="text-right">
                  <p className="font-semibold text-slate-900">
                    {singleCostEstimateLine.totalPrice}
                  </p>
                  {showTotalWeight &&
                    singleCostEstimateLine.totalWeight &&
                    singleCostEstimateLine.totalWeightUnit && (
                      <p className="text-xs text-slate-600 mt-0.5">
                        {formatWeight(singleCostEstimateLine.totalWeight)}{" "}
                        {
                          WEIGHT_UNIT_ABBREVIATION[
                            singleCostEstimateLine.totalWeightUnit
                          ]
                        }
                      </p>
                    )}
                </div>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <tbody>
                    {itemCostEstimateLines.map(
                      (
                        {
                          quantity,
                          unitPrice,
                          totalPrice,
                          totalWeight,
                          totalWeightUnit,
                        },
                        estimateIndex,
                      ) => (
                        <tr key={`${itemTitle}-estimate-${estimateIndex}`}>
                          <td
                            className={`border-r border-slate-400 px-2 py-1.5 text-slate-700 ${
                              estimateIndex + 1 === itemCostEstimateLines.length
                                ? ""
                                : "border-b border-slate-400"
                            }`}
                          >
                            {quantity}
                          </td>
                          <td
                            className={`border-r border-slate-400 px-2 py-1.5 text-slate-700 ${
                              estimateIndex + 1 === itemCostEstimateLines.length
                                ? ""
                                : "border-b border-slate-400"
                            }`}
                          >
                            {unitPrice}
                          </td>
                          <td
                            className={`px-2 py-1.5 text-right font-semibold text-slate-900 ${
                              estimateIndex + 1 === itemCostEstimateLines.length
                                ? ""
                                : "border-b border-slate-400"
                            }`}
                          >
                            {totalPrice}
                            {showTotalWeight &&
                              totalWeight &&
                              totalWeightUnit && (
                                <span className="block text-xs font-normal text-slate-600 mt-0.5">
                                  {formatWeight(totalWeight)}{" "}
                                  {WEIGHT_UNIT_ABBREVIATION[totalWeightUnit]}
                                </span>
                              )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  }

  return null;
};

export function PublicQuoteTemplateIndustrialQuotation({
  quote,
  isExpired,
}: PublicQuoteTemplateIndustrialQuotationProps) {
  const issueDate = formatDate(getQuoteEffectiveDate(quote));
  const expirationDate = formatDate(quote.expirationDate);
  const customerName = quote.customerCompany?.name ?? null;
  const customerEmail = quote.customerCompany?.email ?? null;
  const customerPhone = quote.customerCompany?.phone ?? null;
  const organizationAddress = renderAddressBlock(
    formatAddressLines(quote.organization?.address),
  );
  const firstItemIndex = (quote.lines ?? []).findIndex(
    (line) => line.type === "ITEM",
  );
  const displayTotals = getQuoteDisplayTotals(quote);

  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(quote.lines ?? []),
    [quote.lines],
  );

  const hasSummaryRows = Boolean(
    quote.deliveryTerms ||
      quote.paymentTerms ||
      (quote.showTotals && quote.total),
  );
  const firstSummaryLabelClass = `${TABLE_LABEL_CELL_CLASS} border-t-2 border-t-slate-400`;
  const firstSummaryValueClass = `${TABLE_VALUE_CELL_CLASS} border-t-2 border-t-slate-400`;

  return (
    <div className="space-y-12">
      {quote.enableCoverLetter && quote.coverLetterMarkdown && (
        <Card className="rounded-none border-2 border-slate-900 bg-white shadow-none print:border-2 print:border-slate-900">
          <div
            className="m-3 border border-slate-700 p-5 sm:p-7"
            style={{ breakAfter: "page", pageBreakAfter: "always" }}
          >
            <div className="prose prose-sm max-w-none text-slate-800">
              <MarkdownReact>{quote.coverLetterMarkdown}</MarkdownReact>
            </div>
          </div>
        </Card>
      )}
      <Card className="rounded-none border-2 border-slate-900 bg-white shadow-none print:border-2 print:border-slate-900">
        <div className="m-3 border border-slate-700 p-5 sm:p-7">
          <div className="flex flex-col gap-5 border-b border-slate-500 pb-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2.5">
              <div className="flex items-center gap-3">
                {quote.organization?.logoUrl && (
                  <img
                    src={quote.organization.logoUrl}
                    alt={`${quote.organization.name} logo`}
                    className="h-16 w-16 object-contain"
                  />
                )}
                <div>
                  <p className="text-xl font-semibold text-slate-900">
                    {quote.organization?.name || "Quotation"}
                  </p>
                  {organizationAddress}
                </div>
              </div>
              {isExpired && expirationDate && (
                <p className="text-xs font-semibold text-red-600">
                  Expired on {expirationDate}
                </p>
              )}
            </div>

            <div className="min-w-[240px] text-sm leading-6 text-slate-900">
              <p className="text-2xl font-semibold uppercase tracking-[0.08em]">
                Quotation
              </p>
              <p>
                <span className="font-semibold">Ref:</span> #
                {quote.quoteDisplayNumber}
              </p>
              <p>
                <span className="font-semibold">Revision:</span>{" "}
                {quote.revision}
              </p>
              {issueDate && (
                <p>
                  <span className="font-semibold">Issued:</span> {issueDate}
                </p>
              )}
              {expirationDate && (
                <p>
                  <span className="font-semibold">Valid Until:</span>{" "}
                  {expirationDate}
                </p>
              )}
            </div>
          </div>

          {(customerName || customerEmail || customerPhone) && (
            <div className="border-b border-slate-500 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-700">
                To
              </p>
              {customerName && (
                <p className="text-sm text-slate-800">{customerName}</p>
              )}
              {customerEmail && (
                <p className="text-sm text-slate-700">{customerEmail}</p>
              )}
              {customerPhone && (
                <p className="text-sm text-slate-700">{customerPhone}</p>
              )}
            </div>
          )}

          {quote.shippingAddress && (
            <div className="border-b border-slate-500 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-700">
                Ship To
              </p>
              {renderAddressBlock(formatAddressLines(quote.shippingAddress))}
            </div>
          )}

          <div className="py-5">
            <Table className="table-fixed border-collapse">
              <TableBody>
                {(quote.lines ?? []).map((line, index) =>
                  renderLineRows(
                    line,
                    index,
                    firstItemIndex,
                    quote.showTotals,
                    quote.showTotalWeight ?? false,
                    quote,
                  ),
                )}

                {quote.deliveryTerms && (
                  <TableRow>
                    <TableCell
                      className={
                        hasSummaryRows
                          ? firstSummaryLabelClass
                          : TABLE_LABEL_CELL_CLASS
                      }
                    >
                      Delivery
                    </TableCell>
                    <TableCell
                      className={
                        hasSummaryRows
                          ? firstSummaryValueClass
                          : TABLE_VALUE_CELL_CLASS
                      }
                    >
                      <MarkdownReact>{quote.deliveryTerms}</MarkdownReact>
                    </TableCell>
                  </TableRow>
                )}

                {quote.paymentTerms && (
                  <TableRow>
                    <TableCell
                      className={
                        !quote.deliveryTerms && hasSummaryRows
                          ? firstSummaryLabelClass
                          : TABLE_LABEL_CELL_CLASS
                      }
                    >
                      Payment Terms
                    </TableCell>
                    <TableCell
                      className={
                        !quote.deliveryTerms && hasSummaryRows
                          ? firstSummaryValueClass
                          : TABLE_VALUE_CELL_CLASS
                      }
                    >
                      <MarkdownReact>{quote.paymentTerms}</MarkdownReact>
                    </TableCell>
                  </TableRow>
                )}

                {quote.showTotals && quote.total && (
                  <TableRow>
                    <TableCell
                      className={
                        !quote.deliveryTerms &&
                        !quote.paymentTerms &&
                        hasSummaryRows
                          ? firstSummaryLabelClass
                          : TABLE_LABEL_CELL_CLASS
                      }
                    >
                      Total
                    </TableCell>
                    <TableCell
                      className={
                        !quote.deliveryTerms &&
                        !quote.paymentTerms &&
                        hasSummaryRows
                          ? "border border-slate-400 border-t-2 border-t-slate-400 px-2 py-2 text-right text-sm font-semibold text-slate-900 align-middle"
                          : "border border-slate-400 px-2 py-2 text-right text-sm font-semibold text-slate-900 align-middle"
                      }
                    >
                      {displayTotals.totalFormatted}
                    </TableCell>
                  </TableRow>
                )}
                {quote.showTotals &&
                  quote.showTotalWeight &&
                  totalWeightsByUnit.length > 0 &&
                  totalWeightsByUnit.map(([unit, weight]) => (
                    <TableRow key={unit}>
                      <TableCell className={TABLE_LABEL_CELL_CLASS}>
                        Total Weight ({WEIGHT_UNIT_ABBREVIATION[unit]})
                      </TableCell>
                      <TableCell className="border border-slate-400 px-2 py-2 text-right text-sm text-slate-900 align-middle">
                        {weight.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {quote.isSigned && quote.signature && (
            <div className="mt-5 border-t border-slate-500 pt-5">
              <Table className="table-fixed border-collapse">
                <TableBody>
                  <TableRow>
                    <TableCell className={TABLE_LABEL_CELL_CLASS}>
                      Authorized Signature
                    </TableCell>
                    <TableCell className={TABLE_VALUE_CELL_CLASS}>
                      <div className="font-signature text-3xl text-slate-900">
                        {quote.signature.name}
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={TABLE_LABEL_CELL_CLASS}>
                      Date Signed
                    </TableCell>
                    <TableCell className={TABLE_VALUE_CELL_CLASS}>
                      {new Date(quote.signature.signedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-3 border-t border-slate-500 pt-5 text-sm text-slate-900">
            <p className="mb-3">
              Thank you for the opportunity to quote your casting needs.
            </p>
            <p className="font-semibold">Sincerely,</p>
            <p className="font-semibold">{quote.organization?.name}</p>
            <div className="mt-6 flex justify-center">
              <p className="border border-slate-500 px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-700">
                All quotations are subject to the terms and service provided.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {quote.quoteFooter && (
        <div className="mt-8 border-t-2 border-slate-900 pt-4">
          <p className="text-xs text-slate-700 font-bold uppercase tracking-wide whitespace-pre-wrap">
            {quote.quoteFooter}
          </p>
        </div>
      )}

      {quote.termsOfService && (
        <div
          className="mt-8 pt-2 print:pt-0"
          style={{ breakBefore: "page", pageBreakBefore: "always" }}
        >
          <div className="mx-4 border-t border-slate-400" />
          <Card className="rounded-none border-2 border-slate-900 bg-white shadow-none print:border-2 print:border-slate-900">
            <div className="m-3 border border-slate-700 p-6 sm:p-8">
              <div className="mb-6 border-b border-slate-500 pb-5 text-center">
                <h2 className="text-3xl font-semibold uppercase tracking-[0.12em] text-slate-900">
                  Terms and Conditions
                </h2>
                {quote.organization?.name && (
                  <p className="mt-2 text-xs uppercase tracking-[0.08em] text-slate-600">
                    {quote.organization.name}
                  </p>
                )}
              </div>
              <div className="prose prose-sm prose-slate max-w-none text-slate-800">
                <MarkdownReact>{quote.termsOfService}</MarkdownReact>
              </div>
              <div className="mt-8 flex justify-center border-t border-slate-500 pt-5">
                <p className="border border-slate-500 px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-700">
                  All quotations are subject to the terms and service provided.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
